import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  listProfessions,
  deleteProfession,
  updateProfessionStatus,
} from "../../../../actions/professionActions";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "@chakra-ui/react";
import Table from "../../../../components/shared/Table";
import { Box, Flex } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columnsDesktop } from "../columns/ProfessionListColumns"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/ProfessionListColumnsMobile"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import {
  PROFESSION_DELETE_RESET,
  PROFESSION_UPDATE_RESET,
  PROFESSION_CREATE_RESET,
} from "../../../../constants/professionConstants";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import CreateProfession from "../sidepanels/CreateProfession";
import EditProfession from "../sidepanels/EditProfession";
import { primary } from "../../../../utils/colors";
import { PROFESSION_UPDATE_STATUS_RESET } from "../../../../constants/professionConstants";
import useThemeColors from "../../../../hooks/useThemeColors";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import PageHeader from "../../../../components/layout/PageHeader";
import SearchInput from "../../../../components/input/SearchInput";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import useDebounce from "../../../../hooks/useDebounce";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
import useIsMobile from "../../../../hooks/useIsMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";

const ProfessionListNew = () => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const isMobile = useIsMobile();

  // STATAE
  const [inEmployees, setInEmployees] = useState(false);

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // COLORS
  const { whiteMode, newBorderMode, backgroundLightMode, textPrimaryMode } =
    useThemeColors();

  const [selectedGroup, setSelectedGroup] = useState("");

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // SEARCH
  const [searchTitle, setSearchTitle] = useState("");

  // SIDEPANELS
  const [sidePanelModalOpen, setSidePanelOpen] = useState(false);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);

  // STATE
  const [professionId, setProfessionId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [professionName, setProfessionName] = useState("");

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1);
  };

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const professionList = useSelector((state) => state.professionList);
  const {
    loading,
    error,
    professions,
    pages,
    totalCount,
    countsByStatus,
    totalAveragePrice,
    totalAverageDuration,
    globalTotalAveragePrice,
    globalTotalAverageDuration,
  } = professionList;

  const professionCreate = useSelector((state) => state.professionCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    profession: createdProfession,
  } = professionCreate;

  const professionUpdate = useSelector((state) => state.professionUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = professionUpdate;

  const professionDelete = useSelector((state) => state.professionDelete);

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = professionDelete;

  const professionGroupList = useSelector((state) => state.professionGroupList);
  const { professionGroups } = professionGroupList;

  const professionUpdateStatus = useSelector(
    (state) => state.professionUpdateStatus
  );
  const {
    loading: loadingUpdateStatus,
    error: errorUpdateStatus,
    success: successUpdateStatus,
  } = professionUpdateStatus;

  // FILTERING
  const [isFilterActive, setIsFilterActive] = useState(false);

  const filterConfig = {
    patients: false,
    professions: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: "",
    professionGroup: "",
  });

  // SORTING
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSortChange = (column) => {
    const newSortOrder =
      sortField === column && sortOrder === "asc" ? "desc" : "asc";

    if (sortField !== column || sortOrder !== newSortOrder) {
      // Only dispatch the action if sorting parameters change
      console.log(`Sorting ${column} in ${newSortOrder} order`);

      setSortField(column);
      setSortOrder(newSortOrder);
    }
  };

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    localStorage.setItem("filterSettings_professions", JSON.stringify(filters)); // Example for PatientList
    dispatch(
      listProfessions(
        slug,
        searchTitle,
        1,
        pageSize,
        sortField,
        sortOrder,
        false, // Assuming `fetchAll` is false by default
        filters.status,
        filters.professionGroup
      )
    );

    // console.log("filters", filters);
  };

  // HANDLERS

  const deleteProfessionHandler = (professionId) => {
    dispatch(deleteProfession(slug, professionId));

    if (professions.length === 1) {
      setPage(page - 1);
    }
  };

  // USEEFFECT

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listProfessions(
          slug,
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          false,
          filterParams.status,
          filterParams.professionGroup
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    slug,
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    successUpdate,
    successUpdateStatus,
  ]);

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(t("toasts.professionCreate"), "success", 3000);
      closeSidePanelModal();
    } else if (errorCreate) {
      toastComponent.showToast(
        t("toasts.professionCreateError"),
        "error",
        3000
      );
    }
    dispatch({ type: PROFESSION_CREATE_RESET });
  }, [successCreate, errorCreate]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(t("toasts.professionUpdate"), "success", 3000);
      closeEditSidePanelModal();

      dispatch({ type: PROFESSION_UPDATE_RESET });
    } else if (errorUpdate) {
      toastComponent.showToast(
        t("toasts.professionUpdateError"),
        "error",
        3000
      );
    }
  }, [successUpdate, errorUpdate]);

  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(t("toasts.professionDelete"), "success", 3000);

      dispatch({ type: PROFESSION_DELETE_RESET });
    } else if (errorDelete) {
      toastComponent.showToast(
        t("toasts.professionDeleteError"),
        "error",
        3000
      );
    }
  }, [successDelete, errorDelete]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.professionUpdateStatus"),
        "success",
        3000
      );

      dispatch({ type: PROFESSION_UPDATE_STATUS_RESET });
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.professionUpdateStatusError"),
        "error",
        3000
      );
    }
  }, [successUpdateStatus, errorUpdateStatus]);

  // SIDEPANEL HANDLERS

  const openSidePanelModal = () => {
    setSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const openEditSidePanelModal = () => {
    setEditSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeSidePanelModal = () => {
    setSidePanelOpen(false);
    setEditSidePanelOpen(false);
    setProfessionId("");
    document.body.classList.remove("no-scroll");
  };
  const closeEditSidePanelModal = () => {
    setEditSidePanelOpen(false);
    setSidePanelOpen(false);
    setProfessionId("");
    document.body.classList.remove("no-scroll");
  };

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.on;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.create;
  const hasAccessToEdit =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.update;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.professionsAndProfessionGroups?.delete;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.professionsAndProfessionGroups?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.professions")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching || !professions;

  // LOGS

  // console.log("professions", professions);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.professions")}</title>
        <meta name="description" content={t("pageDescription.professions")} />
      </Helmet>

      {/* SIDEPANELS  START*/}
      {!isMobile ? (
        <SidePanel
          open={sidePanelModalOpen}
          close={closeSidePanelModal}
          title={t("profession.createProfession")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateProfession close={closeSidePanelModal} />
            ) : (
              <UserNoAccess
                pageName={t("noAccess.professionCreate")}
                p="0rem"
              />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={sidePanelModalOpen}
          onClose={closeSidePanelModal}
          title={t("profession.createProfession")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateProfession close={closeSidePanelModal} />
          ) : (
            <UserNoAccess pageName={t("noAccess.professionCreate")} p="0rem" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editSidePanelOpen}
          close={closeEditSidePanelModal}
          title={t("profession.editProfession")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditProfession
              hasEditAccess={hasAccessToEdit}
              close={closeEditSidePanelModal}
              professionId={professionId}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editSidePanelOpen}
          onClose={closeEditSidePanelModal}
          title={t("profession.editProfession")}
          size="full"
        >
          <EditProfession
            hasEditAccess={hasAccessToEdit}
            close={closeEditSidePanelModal}
            professionId={professionId}
          />
        </DrawerNew>
      )}
      {/* SIDEPANELS  END*/}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.professionDelete")}
          deleteTitle={t("profession.deleteProfessionTitle")}
          deleteName={professionName}
          // warningLabel={t("profession.deleteWarning")}
          warningLabel={
            inEmployees
              ? t("profession.deleteWarningInEmployees")
              : t("profession.deleteWarning")
          }
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            if (inEmployees) {
              dispatch(updateProfessionStatus(slug, professionId, "inactive"));
            } else {
              deleteProfessionHandler(professionId);
            }
            setShowDeleteConfirmation(false);
          }}
          // confirmAction={() => {
          //   deleteProfessionHandler(professionId); // Use `userId` set from `MoreButtonLite`
          //   setShowDeleteConfirmation(false);
          // }}
        />
      )}

      {/* MODALS END */}

      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 6rem", "0rem 0rem 6rem", "2rem 2rem 6rem"]}
        >
          <Box
            w="100%"
            h="100%"
            bg={whiteMode}
            overflow="hidden"
            borderRadius="2rem"
          >
            <PageHeader
              label={t("profession.professions")}
              buttonHandler={openSidePanelModal}
              buttonLabel={t("profession.createProfession")}
              filter={
                <></>
                // <TreatmentPlansFilterPopover
                //   onFilterApply={handleFilterApply}
                //   isFilterActive={isFilterActive}
                //   setIsFilterActive={setIsFilterActive}
                //   filterStatusOptions="treatment"
                //   savedFiltersKey="filterSettings_treatmentPlans"
                //   resetFilter={resetFilter}
                //   onResetComplete={() => setResetFilter(false)}
                // />
              }
            />

            <SearchInput
              placeholder={t("profession.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0.5rem 1.5rem", "0 2rem"]}
            />

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={professions}
                      columns={columnsDesktop(
                        slug,
                        dispatch,
                        translate,
                        handleSortChange,
                        updateProfessionStatus,
                        setShowDeleteConfirmation,
                        setProfessionId,
                        setProfessionName,
                        setInEmployees,
                        openEditSidePanelModal,
                        hasAccessToEdit
                      )}
                      noItemsTitle={t("noItems.noProfessions")}
                      noItemsDescription={t("noItems.noProfessionsText")}
                      noItemsButtonLabel={t("noItems.noProfessionsButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(professionId) => {
                        setProfessionId(professionId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={professions}
                      columns={columnsMobile(
                        slug,
                        dispatch,
                        translate,
                        handleSortChange,
                        updateProfessionStatus,
                        setShowDeleteConfirmation,
                        setProfessionId,
                        setProfessionName,
                        setInEmployees,
                        openEditSidePanelModal,
                        hasAccessToEdit
                      )}
                      noItemsTitle={t("noItems.noProfessions")}
                      noItemsDescription={t("noItems.noProfessionsText")}
                      noItemsButtonLabel={t("noItems.noProfessionsButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(professionId) => {
                        setProfessionId(professionId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            )}
          </Box>
        </Flex>
      </Box>
      {/* MAIN END */}
    </>
  );
};

export default ProfessionListNew;
